import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { ItemAliasRecord, ItemAliasType } from '../types/inventoryItem';

export type ItemAliasFormState = {
  itemId: string;
  itemName: string;
  accountId: string;
  accountName: string;
  aliasName: string;
  aliasCode: string;
  aliasType: ItemAliasType;
  notes: string;
};

const initialState = (): ItemAliasFormState => ({
  itemId: '',
  itemName: '',
  accountId: '',
  accountName: '',
  aliasName: '',
  aliasCode: '',
  aliasType: 'Sell',
  notes: '',
});

const mapRecordToForm = (record: ItemAliasRecord): ItemAliasFormState => ({
  itemId: record.itemId,
  itemName: record.item.name,
  accountId: record.accountId,
  accountName: record.account.name,
  aliasName: record.aliasName,
  aliasCode: record.aliasCode ?? '',
  aliasType: record.aliasType,
  notes: record.notes ?? '',
});

// 🔘🔘🔘 PLAIN ATOMS
export const $itemAliasForm = atom<ItemAliasFormState>(initialState());
export const $itemAliasDiscardBaseline = atom<ItemAliasFormState | null>(null);
export const $lastPopulatedItemAliasId = atom<string | undefined>(undefined);
export const $isItemAliasFormPopulated = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA
export const $hasItemAliasUnsavedChanges = atom((get) => {
  const form = get($itemAliasForm);
  const baseline = get($itemAliasDiscardBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

export const $canSubmitItemAliasForm = atom((get) => {
  const form = get($itemAliasForm);
  if (!form.itemId) return false;
  if (!form.accountId) return false;
  if (!form.aliasName.trim()) return false;
  return true;
});

// 🟠🟠🟠 ACTION -> UPDATE item alias form
export const $updateItemAliasForm = atom(
  null,
  (get, set, update: Partial<ItemAliasFormState>) => {
    set($itemAliasForm, { ...get($itemAliasForm), ...update });
  },
);

export const $populateItemAliasFormFromRecord = atom(
  null,
  (_get, set, record: ItemAliasRecord) => {
    const formState = mapRecordToForm(record);
    set($itemAliasForm, formState);
    set($itemAliasDiscardBaseline, formState);
    set($lastPopulatedItemAliasId, record.id);
    set($isItemAliasFormPopulated, true);
  },
);

export const $resetItemAliasFormToBaseline = atom(null, (get, set) => {
  const baseline = get($itemAliasDiscardBaseline);
  if (baseline) {
    set($itemAliasForm, baseline);
  }
});

export const $populateItemAliasFormAction = atom(
  null,
  (get, set, record?: ItemAliasRecord | null) => {
    if (!record) return;

    const currentRecordId = record.id;
    const lastRecordId = get($lastPopulatedItemAliasId);

    if (currentRecordId !== lastRecordId) {
      set($isItemAliasFormPopulated, false);
    }

    if (!get($isItemAliasFormPopulated)) {
      set($populateItemAliasFormFromRecord, record);
    }
  },
);

export const $resetItemAliasForm = atom(null, (_get, set) => {
  set($itemAliasForm, initialState());
  set($itemAliasDiscardBaseline, null);
  set($lastPopulatedItemAliasId, undefined);
  set($isItemAliasFormPopulated, false);
});
